import { p2pDiagram, o2cDiagram } from '../lib/diagrams';

const IMG = {
  warehouse:
    'https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1400&q=80',
  sales:
    'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?auto=format&fit=crop&w=1400&q=80',
  finance:
    'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1400&q=80',
  people:
    'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=80',
  factory:
    'https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=1400&q=80',
  dashboard:
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80',
  office:
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80',
};

// ——————————————————————————————————————————————————————————————
//  MODULE 4 — Overview of SAP Modules (6h)
// ——————————————————————————————————————————————————————————————
const module4 = {
  id: 'm4',
  number: 4,
  title: 'Overview of SAP Modules',
  duration: '6 Hours',
  minutes: 360,
  icon: 'LayoutGrid',
  tagline: 'MM, SD, FICO, HCM, PP — the core SAP functional modules explained.',
  takeaway:
    'You will know the purpose, key transactions and business users of the five most important SAP functional modules — enough to have an intelligent conversation with any SAP consultant or hiring manager.',
  subTopics: [
    {
      id: 'm4-s1',
      title: 'SAP MM — Materials Management',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP MM runs the entire procurement and inventory side of the business: sourcing, purchasing, goods receipt, stock management and invoice verification.',
        },
        { type: 'img', src: IMG.warehouse, caption: 'Figure 4.1 — SAP MM powers warehouses and procurement.' },
        { type: 'h', text: 'What MM handles' },
        {
          type: 'ul',
          items: [
            'Purchase requisitions (PR) — internal request to buy.',
            'Request for Quotation (RFQ) and vendor selection.',
            'Purchase Orders (PO) — formal commitment to vendor.',
            'Goods Receipt (GR) — stock enters the plant (MIGO).',
            'Invoice Verification — 3-way match of PO vs GR vs invoice (MIRO).',
            'Inventory management — physical stock, reservations, transfers.',
            'Material Master and Vendor Master maintenance.',
          ],
        },
        {
          type: 'table',
          headers: ['Who uses it', 'Typical T-Codes'],
          rows: [
            ['Buyer / Procurement analyst', 'ME21N, ME22N, ME23N'],
            ['Warehouse associate', 'MIGO, MB51, MB52'],
            ['AP clerk', 'MIRO, MR8M'],
            ['Master data steward', 'MM01, XK01'],
          ],
        },
      ],
    },
    {
      id: 'm4-s2',
      title: 'SAP SD — Sales & Distribution',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP SD runs the customer-facing order-to-cash flow — from quote, through sales order, delivery and billing, to the receivable on the books.',
        },
        { type: 'img', src: IMG.sales, caption: 'Figure 4.2 — SD covers the journey from sales quote to customer invoice.' },
        { type: 'h', text: 'What SD handles' },
        {
          type: 'ul',
          items: [
            'Customer inquiries and quotations (VA11 / VA21).',
            'Sales Orders (VA01), returns orders, credit/debit memos.',
            'Availability Check (ATP) — can we promise the date?',
            'Outbound Delivery &amp; Goods Issue (VL01N).',
            'Pricing &amp; condition techniques (discounts, surcharges, taxes).',
            'Billing (VF01) and hand-off to FI for A/R.',
            'Customer Master maintenance (XD01).',
          ],
        },
        {
          type: 'callout',
          variant: 'tip',
          title: 'SD pricing is legendary',
          text:
            'Ask any SAP SD consultant about pricing procedure, condition records and access sequences — it is arguably the most powerful (and complex) part of SAP.',
        },
      ],
    },
    {
      id: 'm4-s3',
      title: 'SAP FICO — Finance & Controlling',
      readingTime: '30 min',
      blocks: [
        {
          type: 'p',
          text:
            'FICO is two sisters bundled together. FI (Financial Accounting) is external reporting — the legally required books. CO (Controlling) is internal management accounting — profitability, costs and budgets.',
        },
        { type: 'img', src: IMG.finance, caption: 'Figure 4.3 — FICO is the heart of SAP; every other module eventually posts here.' },
        { type: 'h', text: 'FI (Financial Accounting)' },
        {
          type: 'ul',
          items: [
            'General Ledger (GL) — chart of accounts, trial balance.',
            'Accounts Payable (AP) — vendor invoices, payments.',
            'Accounts Receivable (AR) — customer invoices, incoming payments.',
            'Asset Accounting (AA) — depreciation of fixed assets.',
            'Banking — bank accounts, electronic bank statements.',
            'Taxes and statutory reporting.',
          ],
        },
        { type: 'h', text: 'CO (Controlling)' },
        {
          type: 'ul',
          items: [
            'Cost Element Accounting.',
            'Cost Center &amp; Internal Order Accounting.',
            'Product Costing — what did this product really cost to make?',
            'Profitability Analysis (CO-PA) — margin by customer, channel, region.',
            'Profit Center Accounting — P&amp;L per business unit.',
          ],
        },
        {
          type: 'table',
          headers: ['Who uses FICO', 'Typical T-Codes'],
          rows: [
            ['GL accountant', 'FB01, FB50, F-03, FBL3N'],
            ['AP / AR clerk', 'FB60, FB70, F110'],
            ['Asset accountant', 'AS01, ABT1N, AFAB'],
            ['Controller', 'KSB1, KE30, KSU5'],
          ],
        },
      ],
    },
    {
      id: 'm4-s4',
      title: 'SAP HCM / SuccessFactors — Human Capital Management',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP HCM manages the full employee lifecycle — from hire to retire. In the classic on-premise world it is called SAP HCM (aka HR); in the cloud it is called SAP SuccessFactors.',
        },
        { type: 'img', src: IMG.people, caption: 'Figure 4.4 — HCM / SuccessFactors tracks every employee event in the company.' },
        { type: 'h', text: 'Sub-modules' },
        {
          type: 'table',
          headers: ['Sub-module', 'Purpose'],
          rows: [
            ['PA — Personnel Administration', 'Hire, transfer, promote, separate employees.'],
            ['OM — Organizational Management', 'Positions, reporting lines, org chart.'],
            ['PY — Payroll', 'Gross-to-net, statutory deductions, pay slips.'],
            ['TM — Time Management', 'Attendance, shifts, leave, time evaluation.'],
            ['PD — Personnel Development', 'Training, appraisals, career planning.'],
            ['Travel Management', 'Trip requests, expenses (often SAP Concur).'],
          ],
        },
        {
          type: 'callout',
          variant: 'info',
          title: 'Direction of travel',
          text:
            'Most new customers adopt SAP SuccessFactors (cloud). Recruiting, onboarding, performance and learning are delivered as best-of-breed modules that integrate with core ERP.',
        },
      ],
    },
    {
      id: 'm4-s5',
      title: 'SAP PP — Production Planning',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP PP runs the factory — what to make, when, from which materials and on which machine. It connects tightly to MM (raw material supply) and SD (finished-goods demand).',
        },
        { type: 'img', src: IMG.factory, caption: 'Figure 4.5 — PP orchestrates the production floor.' },
        {
          type: 'ul',
          items: [
            'Bill of Materials (BOM) — parts list for every product.',
            'Routing — sequence of work-centre operations.',
            'Work Centres — machines / lines capacity data.',
            'Demand Management — forecasts, planned independent requirements.',
            'MRP (Material Requirements Planning) — the planning engine.',
            'Production Orders — the shop-floor work order.',
            'Confirmations &amp; Goods Receipt of finished goods.',
          ],
        },
        {
          type: 'callout',
          variant: 'example',
          title: 'Mini-scenario',
          text:
            'A sales forecast for 10,000 TVs in Q2 ⇒ PP’s MRP run explodes the BOM ⇒ creates planned orders for panels, chipboards and packaging ⇒ the buyer converts those into POs in MM ⇒ PP schedules assembly lines to make the TVs.',
        },
      ],
    },
    {
      id: 'm4-s6',
      title: 'Other Important SAP Modules (bonus)',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'Beyond the big five, SAP has dozens of modules. You don’t need to master them all, but knowing they exist helps you have smarter conversations.',
        },
        {
          type: 'table',
          headers: ['Module', 'Area'],
          rows: [
            ['SAP QM', 'Quality Management — inspections, certifications'],
            ['SAP PM', 'Plant Maintenance — maintenance orders, equipment'],
            ['SAP WM / EWM', 'Warehouse Management — bins, picking, putaway'],
            ['SAP PS', 'Project System — project planning &amp; costing'],
            ['SAP TM', 'Transportation Management — freight, carriers'],
            ['SAP BW / BW4', 'Data Warehousing &amp; reporting'],
            ['SAP CRM / C4C', 'Customer Relationship Management'],
            ['SAP SRM / Ariba', 'Supplier relationship &amp; sourcing'],
            ['SAP GRC', 'Governance, Risk &amp; Compliance'],
          ],
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'Which SAP module is used to raise a Purchase Order?',
      options: ['SAP SD', 'SAP MM', 'SAP FI', 'SAP PP'],
      correct: 1,
      explanation: 'ME21N (Create PO) is in SAP MM.',
    },
    {
      q: 'Which module owns the Sales Order (VA01)?',
      options: ['SD', 'MM', 'CO', 'HCM'],
      correct: 0,
      explanation: 'Sales Orders belong to SAP SD (Sales & Distribution).',
    },
    {
      q: 'A 3-way match is performed between…',
      options: [
        'PO, GR and Invoice',
        'Sales Order, Delivery, Invoice',
        'BOM, Routing, Work Center',
        'GL, AR, AP',
      ],
      correct: 0,
      explanation: 'Invoice Verification (MIRO) matches PO ↔ Goods Receipt ↔ Vendor Invoice.',
    },
    {
      q: 'Which of these is NOT part of FICO?',
      options: ['General Ledger', 'Accounts Payable', 'MRP run', 'Asset Accounting'],
      correct: 2,
      explanation: 'MRP is part of PP (Production Planning), not FICO.',
    },
    {
      q: 'Which cloud product replaces classic SAP HCM for many customers?',
      options: ['SAP Ariba', 'SAP SuccessFactors', 'SAP Concur', 'SAP Fieldglass'],
      correct: 1,
      explanation: 'SuccessFactors is SAP’s cloud HCM suite.',
    },
    {
      q: 'Which module maintains the Bill of Materials (BOM)?',
      options: ['SD', 'MM', 'PP', 'FI'],
      correct: 2,
      explanation: 'The BOM is a core master object in SAP PP.',
    },
  ],
};

// ——————————————————————————————————————————————————————————————
//  MODULE 5 — End-to-End Business Processes in SAP (8h)
// ——————————————————————————————————————————————————————————————
const module5 = {
  id: 'm5',
  number: 5,
  title: 'End-to-End Business Processes in SAP',
  duration: '8 Hours',
  minutes: 480,
  icon: 'Workflow',
  tagline: 'The four core "mega-processes" every SAP consultant must know.',
  takeaway:
    'You will be able to describe Procure-to-Pay, Order-to-Cash, Hire-to-Retire and Record-to-Report end-to-end, name the key steps, T-Codes and integration points across modules.',
  subTopics: [
    {
      id: 'm5-s1',
      title: 'Procure-to-Pay (P2P)',
      readingTime: '30 min',
      blocks: [
        {
          type: 'p',
          text:
            'P2P is the journey from "we need something" to "we paid the vendor". It spans MM + FI.',
        },
        { type: 'img', src: p2pDiagram, caption: 'Figure 5.1 — P2P touches 6 classic steps.' },
        {
          type: 'ol',
          items: [
            'Purchase Requisition (PR) — internal demand, often auto-generated by MRP.',
            'Source determination / RFQ — pick vendor, possibly quotation.',
            'Purchase Order (ME21N) — formal commitment to the vendor.',
            'Goods Receipt (MIGO) — stock enters inventory, accounting posts GR/IR.',
            'Invoice Verification (MIRO) — 3-way match, AP sub-ledger updated.',
            'Payment Run (F110) — vendor is paid; cash decreases, vendor clears.',
          ],
        },
        {
          type: 'callout',
          variant: 'example',
          title: 'Accounting impact at each step',
          text:
            'GR: Stock Dr / GR-IR Cr. Invoice: GR-IR Dr / Vendor Cr. Payment: Vendor Dr / Bank Cr. Three hand-offs, fully automated.',
        },
      ],
    },
    {
      id: 'm5-s2',
      title: 'Order-to-Cash (O2C)',
      readingTime: '30 min',
      blocks: [
        {
          type: 'p',
          text:
            'O2C is the customer-facing mirror of P2P — from "a customer wants to buy" to "we collected the cash". It spans SD + FI.',
        },
        { type: 'img', src: o2cDiagram, caption: 'Figure 5.2 — O2C end-to-end.' },
        {
          type: 'ol',
          items: [
            'Inquiry / Quotation — customer asks, we quote.',
            'Sales Order (VA01) — confirmed order entered.',
            'Delivery (VL01N) — pick, pack, ship.',
            'Goods Issue — stock reduces, COGS posts.',
            'Billing (VF01) — customer invoice, A/R created.',
            'Incoming Payment (F-28) — cash received, customer cleared.',
          ],
        },
        { type: 'img', src: IMG.sales, caption: 'Figure 5.3 — O2C in the real world.' },
      ],
    },
    {
      id: 'm5-s3',
      title: 'Hire-to-Retire (H2R)',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'H2R spans the whole employee lifecycle — from recruitment to retirement. It crosses HCM / SuccessFactors + FICO (for payroll postings).',
        },
        {
          type: 'ol',
          items: [
            'Workforce planning &amp; requisition approval.',
            'Recruitment &amp; offer (SuccessFactors Recruiting).',
            'Onboarding &amp; master data creation (PA30 / SF Onboarding).',
            'Time capture &amp; attendance (CATS, Time Management).',
            'Payroll run (PC00_M10_CALC or country-specific).',
            'Posting payroll results to FI (PCP0 / payroll posting).',
            'Performance &amp; training (SF Performance, SF Learning).',
            'Separation, final settlement, archival.',
          ],
        },
        { type: 'img', src: IMG.people, caption: 'Figure 5.4 — H2R is the longest relationship the business has with any record.' },
      ],
    },
    {
      id: 'm5-s4',
      title: 'Record-to-Report (R2R)',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'R2R is the finance mega-process — ensuring every transaction is captured, reconciled and reported to stakeholders and regulators.',
        },
        {
          type: 'ol',
          items: [
            'Journal entries — manual and automated (FB01, FB50).',
            'Sub-ledger reconciliation — AP, AR, Assets, Inventory.',
            'Inter-company eliminations.',
            'Period-end accruals, provisions, FX revaluation.',
            'Closing activities — open period, lock postings, run depreciation.',
            'Consolidation — SAP Group Reporting / BPC.',
            'Statutory &amp; management reporting — dashboards, P&amp;L, balance sheet, cash flow.',
          ],
        },
        { type: 'img', src: IMG.finance, caption: 'Figure 5.5 — R2R closes the loop that all other processes feed.' },
      ],
    },
    {
      id: 'm5-s5',
      title: 'Case Study — Running a Manufacturing Company',
      readingTime: '30 min',
      blocks: [
        {
          type: 'p',
          text:
            'Let’s follow "Aurora Motors", a fictional EV maker. Aurora runs SAP S/4HANA. A single customer order triggers all four mega-processes.',
        },
        {
          type: 'ol',
          items: [
            'Sales Order for 50 electric SUVs (O2C — SD).',
            'MRP run explodes BOM, creates planned orders for chassis, batteries, tyres.',
            'Planned orders become Purchase Requisitions ⇒ POs ⇒ Goods Receipts (P2P — MM + FI).',
            'Production Orders schedule 50 cars on line-2 (PP).',
            'Payroll is run for line workers that month (H2R — HCM + FI).',
            'Cars are delivered &amp; billed; A/R rises (O2C — SD + FI).',
            'Month-end close: accruals, depreciation, financial reporting (R2R — FI + CO).',
          ],
        },
        { type: 'img', src: IMG.factory, caption: 'Figure 5.6 — One order lights up every module.' },
        {
          type: 'callout',
          variant: 'tip',
          title: 'Interview gold',
          text:
            'In SAP interviews, being able to walk through a mini case study like Aurora — naming the modules and T-Codes at each step — is a huge differentiator.',
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'The last step of P2P is…',
      options: ['Purchase Requisition', 'Goods Receipt', 'Invoice Verification', 'Vendor Payment'],
      correct: 3,
      explanation: 'P2P finishes when the vendor is paid (F110).',
    },
    {
      q: 'Goods Issue in an outbound delivery posts which accounting entry?',
      options: [
        'Stock Dr / COGS Cr',
        'COGS Dr / Stock Cr',
        'A/R Dr / Revenue Cr',
        'Revenue Dr / Stock Cr',
      ],
      correct: 1,
      explanation: 'When we ship, inventory reduces and cost of goods sold is booked.',
    },
    {
      q: 'Record-to-Report primarily belongs to which area?',
      options: ['SD', 'MM / P2P', 'FICO', 'PP'],
      correct: 2,
      explanation: 'R2R is a FICO-led process that consolidates all postings.',
    },
    {
      q: 'Which process owns the employee lifecycle?',
      options: ['P2P', 'O2C', 'H2R', 'R2R'],
      correct: 2,
      explanation: 'Hire-to-Retire (H2R) covers recruitment through separation.',
    },
    {
      q: 'In O2C, which step creates the A/R on the books?',
      options: ['Sales Order', 'Delivery', 'Goods Issue', 'Billing'],
      correct: 3,
      explanation: 'Billing (VF01) issues the invoice and creates A/R.',
    },
    {
      q: 'An MRP run typically triggers…',
      options: [
        'New customer master records',
        'Planned orders and purchase requisitions',
        'Payroll postings',
        'Bank reconciliations',
      ],
      correct: 1,
      explanation: 'MRP explodes demand into planned orders (for PP) and PRs (for MM).',
    },
  ],
};

export const MODULES_PART2 = [module4, module5];
